/**
 * Canonical admin "view" routes for core entities.
 *
 * Centralized so that entity references across the app (a test name in a
 * "used in" list, a learning-path chip, a resource row) link to a real page
 * instead of rendering as dead text or a `#` dead click — and so the route
 * strings aren't re-typed at every call site.
 *
 * Each id-based builder returns `null` when the id is missing, so callers can
 * fall back to plain text rather than emitting a dead-end link:
 *
 *   const href = adminTestHref(test.id);
 *   return href ? <Link href={href}>{test.name}</Link> : <span>{test.name}</span>;
 */

import { getAdminTestHref } from "@/lib/agent-tools/content-links";

const ADMIN_BASE = "/workspaces/admin";

/**
 * Pass the test record (or an already-resolved type string) whenever you have
 * it. Q&A, glossary and capability-map tests each have their own admin route,
 * and `/tests/:id` renders their questions as "No content available" because it
 * prints `Question.content`, which those never set — their content lives on the
 * linked Q&A / glossary / capability-map record (SAL-3039).
 *
 * The second argument only works if the test was loaded with
 * TEST_TYPE_SELECT_FIELDS (`select:`) or TEST_TYPE_INCLUDE (`include:`) —
 * without the questions probe every test resolves as standard. The route map
 * itself stays in content-links.js so there is only one copy of it.
 */
export function adminTestHref(id, test) {
  return id != null ? getAdminTestHref(id, test ?? {}) : null;
}

export function adminLearningPathHref(id) {
  return id != null ? `${ADMIN_BASE}/learning-paths/${id}` : null;
}

/** A learning path's enrollments list. */
export function adminLearningPathAssignmentsHref(pathId) {
  const path = adminLearningPathHref(pathId);
  return path ? `${path}/assignments` : null;
}

/** One member's progress through a learning path. */
export function adminLearningPathEnrollmentHref(pathId, enrollmentId) {
  const list = adminLearningPathAssignmentsHref(pathId);
  return list && enrollmentId != null ? `${list}/${enrollmentId}` : null;
}

export function adminResourceHref(id) {
  return id != null ? `${ADMIN_BASE}/resources/${id}` : null;
}

export function adminRolePlayHref(id) {
  return id != null ? `${ADMIN_BASE}/role-plays/${id}` : null;
}

export function adminCreateFaqTestHref(faqId) {
  return faqId != null
    ? `${ADMIN_BASE}/faq-tests?faqId=${encodeURIComponent(String(faqId))}`
    : null;
}

export function adminPersonHref(id) {
  return id != null ? `${adminPeopleHref()}/${id}` : null;
}

export function adminRubricHref(id) {
  return id != null ? `${adminRubricsHref()}/${id}` : null;
}

export function adminRubricEditHref(id) {
  const page = adminRubricHref(id);
  return page ? `${page}/edit` : null;
}

// Library indexes, for a link to "all of them" rather than one item.
export function adminObjectionsHref() {
  return `${ADMIN_BASE}/data/objections`;
}

export function adminPersonasHref() {
  return `${ADMIN_BASE}/data/personas`;
}

export function adminRubricsHref() {
  return `${ADMIN_BASE}/data/rubrics`;
}

export function adminPeopleHref() {
  return `${ADMIN_BASE}/people`;
}

export function adminFaqsHref() {
  return `${ADMIN_BASE}/faqs`;
}

export function adminGlossaryHref() {
  return `${ADMIN_BASE}/glossary`;
}

export function adminFaqHref(id) {
  return id != null ? `${adminFaqsHref()}/${id}` : null;
}

export function adminGlossaryTermHref(id) {
  return id != null ? `${adminGlossaryHref()}/${id}` : null;
}
